import { useDraggable } from "@dnd-kit/core";
import { GripVertical, MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { PageFormAction } from "@/pages/tina/festivals/lib/page-form";
import { trayDragId, trayHint } from "@/pages/tina/festivals/lib/page-request";

interface TrayProps {
  names: string[];
  /** How many of the artists the page listed without a day are placed already. */
  placedCount: number;
  /** The days a chip can be put on, with their numbers. */
  dayLabels: { key: string; number: number }[];
  editable: boolean;
  dispatch: (action: PageFormAction) => void;
}

/** A chip's grip and name: the part a curator grabs, and what follows the pointer while it is dragged (`lifted`). */
export function ChipFace({
  name,
  lifted = false,
}: {
  name: string;
  lifted?: boolean;
}) {
  return (
    <span
      className={cn(
        "flex items-center gap-1 whitespace-nowrap text-sm",
        lifted &&
          "w-max cursor-grabbing rounded-full border border-primary bg-background px-3 py-1 shadow-lg ring-2 ring-primary/40",
      )}
    >
      <GripVertical className="h-3 w-3 text-muted-foreground" aria-hidden />
      {name}
    </span>
  );
}

function Chip({
  name,
  dayLabels,
  editable,
  dispatch,
}: Pick<TrayProps, "dayLabels" | "editable" | "dispatch"> & { name: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: trayDragId(name),
    disabled: !editable,
  });
  return (
    <li
      className={cn(
        "flex items-center gap-1 rounded-full border bg-background px-3 py-1 text-sm transition-opacity",
        isDragging && "border-dashed opacity-40",
      )}
    >
      <span
        ref={setNodeRef}
        className={cn("touch-none", editable && "cursor-grab")}
        {...attributes}
        {...listeners}
      >
        <ChipFace name={name} />
      </span>
      {editable ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label={`Place ${name}`}
            >
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {dayLabels.map(({ key, number }) => (
              <DropdownMenuItem
                key={key}
                onSelect={() => dispatch({ type: "place", name, key })}
              >
                Put on Day {number}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem
              onSelect={() => dispatch({ type: "drop_tray", name })}
            >
              Remove from the list
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}

/** The artists the page lists but not on which day: a dashed box of chips to drag onto a day, or to place from a menu. */
export function Tray({
  names,
  placedCount,
  dayLabels,
  editable,
  dispatch,
}: TrayProps) {
  if (names.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 rounded-lg border-2 border-dashed border-amber-400 p-3">
      <p className="text-sm">
        <b>Not placed on a day yet.</b> {trayHint(names.length, placedCount)}
      </p>
      <ul className="flex flex-wrap gap-2">
        {names.map((name) => (
          <Chip
            key={name}
            name={name}
            dayLabels={dayLabels}
            editable={editable}
            dispatch={dispatch}
          />
        ))}
      </ul>
      {editable ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => dispatch({ type: "place_all" })}
        >
          Put all on every day
        </Button>
      ) : null}
    </div>
  );
}
